import { useState, useRef, useEffect } from "react";
import { Ban, Search, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";

interface TickerResult {
  symbol: string;
  name: string;
  type: string;
}

interface TickerSearchProps {
  excludedStocks: string[];
  onAdd: (ticker: string) => void;
  onRemove: (ticker: string) => void;
}

const TickerSearch = ({ excludedStocks, onAdd, onRemove }: TickerSearchProps) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TickerResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchTickers = async (q: string) => {
    if (q.length < 1) { setResults([]); setShowDropdown(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("ticker-search", { body: { query: q } });
      if (error) throw error;
      const quotes: TickerResult[] = data?.quotes || [];
      setResults(quotes);
      setShowDropdown(quotes.length > 0);
    } catch {
      setResults([]); setShowDropdown(false);
    } finally { setLoading(false); }
  };

  const handleChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchTickers(value), 300);
  };

  const selectTicker = (symbol: string) => {
    if (!excludedStocks.includes(symbol)) onAdd(symbol);
    setQuery(""); setResults([]); setShowDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const ticker = query.trim().toUpperCase();
      if (ticker && !excludedStocks.includes(ticker)) {
        onAdd(ticker); setQuery(""); setResults([]); setShowDropdown(false);
      }
    }
  };

  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-display font-medium mb-1">
        <Ban className="w-4 h-4 text-gold" />
        {t("ticker.label")}
        <span className="text-[10px] text-muted-foreground font-normal ms-1">{t("form.optional")}</span>
      </label>
      <p className="text-xs text-muted-foreground mb-3">{t("ticker.desc")}</p>

      <div ref={containerRef} className="relative mb-3">
        <div className="relative">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={t("ticker.placeholder")}
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => results.length > 0 && setShowDropdown(true)}
            className="w-full h-10 rounded-lg bg-secondary border border-border ps-9 pe-9 text-foreground font-display text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 transition-all"
          />
          {loading && <Loader2 className="absolute end-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />}
        </div>

        {showDropdown && (
          <div className="absolute z-50 top-full mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-border bg-card shadow-xl">
            {results.map((r) => {
              const isExcluded = excludedStocks.includes(r.symbol);
              return (
                <button
                  key={r.symbol} disabled={isExcluded} onClick={() => selectTicker(r.symbol)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-start text-sm font-display transition-colors ${
                    isExcluded ? "opacity-40 cursor-not-allowed" : "hover:bg-secondary cursor-pointer"
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-foreground shrink-0">{r.symbol}</span>
                    <span className="text-muted-foreground truncate text-xs">{r.name}</span>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                    r.type === "crypto" ? "bg-gold/10 text-gold border border-gold/20" : "bg-primary/10 text-primary border border-primary/20"
                  }`}>
                    {r.type === "crypto" ? "Crypto" : "Stock"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {excludedStocks.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {excludedStocks.map((ticker) => (
            <span key={ticker} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-red-400/30 bg-red-400/10 text-red-400 text-xs font-display">
              {ticker}
              <button onClick={() => onRemove(ticker)} className="hover:text-red-300">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default TickerSearch;
